//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';

import FacebookPageTask from '@models/FacebookPageTask';
import FacebookUserPermission from '@models/FacebookUserPermission';

export enum CuralateInstagramFeature {
  PromotedPostsCreate = 'PromotedPostsCreate',
  InstagramAutoPosting = 'InstagramAutoPosting',
  InstagramInsights = 'InstagramInsights',
  InstagramMentions = 'InstagramMentions',
  InstagramHashtags = 'InstagramHashtags',
  InstagramShopTags = 'InstagramShopTags',
  InstagramManageMessages = 'InstagramManageMessages',
}

export enum CuralateFacebookFeature {
  FacebookScheduling = 'FacebookScheduling',
  FacebookInsights = 'FacebookInsights',
  FacebookAudiences = 'FacebookAudiences',
}

interface ICuralateFbIgFeatureInfo {
  title: string;
  requirements: ICuralateFacebookFeatureRequirements[];
}

export interface ICuralateFacebookFeatureRequirements {
  permissions: FacebookUserPermission[];
  tasks: FacebookPageTask[];
  feature?: FeatureNames;
}

export const CuralateInstagramFeatureInfo: Record<
  CuralateInstagramFeature,
  ICuralateFbIgFeatureInfo
> = {
  [CuralateInstagramFeature.PromotedPostsCreate]: {
    title: 'Promote Instagram Posts',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.AdsManagement,
          FacebookUserPermission.AdsRead,
        ],
        tasks: [FacebookPageTask.Advertise, FacebookPageTask.Analyze],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramAutoPosting]: {
    title: 'Automatic Publishing to Instagram',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.InstagramBasic,
          FacebookUserPermission.InstagramContentPublish,
        ],
        tasks: [FacebookPageTask.CreateContent],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramInsights]: {
    title: 'View Instagram Insights',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.InstagramBasic,

          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.InstagramManageInsights,
        ],
        tasks: [],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramMentions]: {
    title: 'Track Instagram Mentions & Tags',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.InstagramBasic,

          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.InstagramManageComments,
        ],
        tasks: [],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramHashtags]: {
    title: 'Track Instagram Hashtags',
    requirements: [
      {
        permissions: [FacebookUserPermission.InstagramBasic],
        tasks: [],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramShopTags]: {
    title: 'Track Instagram Shop Tags',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.AdsManagement,
          FacebookUserPermission.CatalogManagement,
        ],
        tasks: [],
        feature: FeatureNames.InstagramShopTagsInsights,
      },
      {
        permissions: [FacebookUserPermission.InstagramShoppingTagProducts],
        tasks: [],
      },
    ],
  },
  [CuralateInstagramFeature.InstagramManageMessages]: {
    title: 'Track Instagram messages',
    requirements: [
      {
        permissions: [FacebookUserPermission.InstagramManageMessages],
        tasks: [],
      },
    ],
  },
};

export const CuralateFacebookFeatureInfo: Record<
  CuralateFacebookFeature,
  ICuralateFbIgFeatureInfo
> = {
  [CuralateFacebookFeature.FacebookScheduling]: {
    title: 'Schedule Facebook Posts',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.PagesManagePosts,
          FacebookUserPermission.PagesManageEngagement,
        ],
        tasks: [FacebookPageTask.CreateContent],
      },
    ],
  },
  [CuralateFacebookFeature.FacebookInsights]: {
    title: 'View Facebook Insights',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.ReadInsights,
        ],
        tasks: [],
      },
    ],
  },
  [CuralateFacebookFeature.FacebookAudiences]: {
    title: 'Facebook Lookalike Audiences',
    requirements: [
      {
        permissions: [
          FacebookUserPermission.PagesManageAds,
          FacebookUserPermission.PagesManageMetadata,
          FacebookUserPermission.PagesReadEngagement,
          FacebookUserPermission.PagesReadUserContent,

          FacebookUserPermission.AdsManagement,
          FacebookUserPermission.AdsRead,
        ],
        tasks: [],
        feature: FeatureNames.FacebookAdsCuralateLookalikeAudience,
      },
    ],
  },
};

export const CuralateFbIgFeatureInfo = {
  ...CuralateFacebookFeatureInfo,
  ...CuralateInstagramFeatureInfo,
};
